/**
 * The date format is an Organization setting, MM/DD/YYYY by default, read by
 * one formatter (piece 15; product owner, 2026-09-10). 🔨 A screen never
 * picks a locale of its own: the setting decides the order of the parts, and
 * the viewer's zone decides which day it is. 🔨 No formatter defaults the
 * format (T531): a caller that names none fails the typecheck, where a default
 * printed MM/DD/YYYY on the pages that forgot the setting.
 */
import { cachedFormatter, calendarDayInZone, zoneAbbreviation } from '../zonedTime';

export const DATE_FORMAT_KEY = 'display.dateFormat';
export const DATE_FORMATS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];
export const DEFAULT_DATE_FORMAT: DateFormat = 'MM/DD/YYYY';

export function asDateFormat(value: unknown): DateFormat {
  return (DATE_FORMATS as readonly string[]).includes(String(value)) ? (value as DateFormat) : DEFAULT_DATE_FORMAT;
}

function parts(instant: Date, zone: string): { year: string; month: string; day: string } | null {
  if (Number.isNaN(instant.getTime())) return null;
  try {
    const day = calendarDayInZone(instant, zone);
    return { year: day.slice(0, 4), month: day.slice(5, 7), day: day.slice(8, 10) };
  } catch {
    return null;
  }
}

/** A calendar date in the viewer's zone, in the Organization's format: 09/10/2026, 10/09/2026 or 2026-09-10. */
export function formatDate(instant: Date | string, zone: string, format: DateFormat): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  const p = parts(date, zone);
  if (!p) return String(instant);
  if (format === 'DD/MM/YYYY') return `${p.day}/${p.month}/${p.year}`;
  if (format === 'YYYY-MM-DD') return `${p.year}-${p.month}-${p.day}`;
  return `${p.month}/${p.day}/${p.year}`;
}

/** A date-only column arrives as midnight UTC and reads as that date, never as a time of night somewhere west of it. */
export function formatDateOnly(iso: string, format: DateFormat): string {
  return formatDate(iso, 'UTC', format);
}

/** The date in the Organization's format followed by the time in the viewer's zone: 09/10/2026 8:00 AM. */
export function formatDateTime(instant: Date | string, zone: string, format: DateFormat): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return String(instant);
  try {
    const time = cachedFormatter(zone, { hour: 'numeric', minute: '2-digit' }).format(date);
    return `${formatDate(date, zone, format)} ${time}`;
  } catch {
    return String(instant).slice(0, 16).replace('T', ' ');
  }
}

/**
 * An ISO instant as the reader's short date and time in their own zone with the
 * zone named after it, 09/12/2026 5:00 PM EST, or the raw text when it is not one
 * (T2428). A date column arrives as midnight UTC and reads as its date, never as
 * a time of night somewhere west of it; a caller holding a true instant, a note
 * written at exactly midnight UTC among them, passes `instantOnly` and keeps its
 * time (T2424). The web's tile rows and the phone's pin panel both read it here.
 */
export function whenText(at: string | null | undefined, zone: string, format: DateFormat, instantOnly = false): string {
  if (!at) return '';
  const instant = new Date(at);
  if (Number.isNaN(instant.getTime())) return at;
  if (!instantOnly && at.endsWith('T00:00:00.000Z')) return formatDateOnly(at, format);
  try {
    return `${formatDateTime(instant, zone, format)} ${zoneAbbreviation(instant, zone)}`;
  } catch {
    // silent: a zone with no abbreviation draws the time without one
    return formatDateTime(instant, zone, format);
  }
}
